import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Text,
  useToast,
  Box,
  useColorModeValue,
  Checkbox,
  CheckboxGroup,
  Stack,
  FormErrorMessage
} from '@chakra-ui/react';
import apiClient from '../lib/api/apiClient';

interface JoinTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  team?: any;
  league?: any;
}

const AVAILABLE_ROLES = [
  'Co-Owner',
  'General Manager',
  'Manager',
  'Hitting Coach',
  'Pitching Coach',
  'Scout',
  'Fan/Shareholder'
];

export const JoinTeamModal: React.FC<JoinTeamModalProps> = ({
  isOpen,
  onClose,
  team,
  league
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [confirmEmailAddress, setConfirmEmailAddress] = useState('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [customMessage, setCustomMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const toast = useToast();

  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!emailAddress.trim()) {
      newErrors.emailAddress = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailAddress)) {
      newErrors.emailAddress = 'Please enter a valid email address';
    }

    if (!confirmEmailAddress.trim()) {
      newErrors.confirmEmailAddress = 'Please confirm your email address';
    } else if (emailAddress !== confirmEmailAddress) {
      newErrors.confirmEmailAddress = 'Email addresses do not match';
    }

    if (selectedRoles.length === 0) {
      newErrors.roles = 'Please select at least one role';
    }

    if (!customMessage.trim()) {
      newErrors.message = 'Message to team owner is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      
      await apiClient.post('/team/join-request', {
        team_id: team?.id,
        league_id: league?.id,
        first_name: firstName,
        last_name: lastName,
        email_address: emailAddress,
        roles: selectedRoles,
        message: customMessage
      });

      toast({
        title: 'Request Sent',
        description: `Your request to join ${team?.name || 'the team'} has been sent to the team owner. You should receive an email response.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Reset form and close modal
      resetForm();
      onClose();
    } catch (error: any) {
      console.error('Error sending join request:', error);
      toast({
        title: 'Request Failed',
        description: error?.response?.data?.message || 'Failed to send your request. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmailAddress('');
    setConfirmEmailAddress('');
    setSelectedRoles([]);
    setCustomMessage('');
    setErrors({});
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent bg={bgColor} maxH="90vh" overflowY="auto">
        <ModalHeader color={textColor}>
          Join a Team
        </ModalHeader>
        <ModalCloseButton color={textColor} />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="md" color={textColor} mb={2}>
              You are requesting to join the <strong>{team?.name || 'team'}</strong>. Please provide your contact information as well as the role(s) you're interested in. The Team Owner will be notified, and you should receive an email response.
            </Text>

            <FormControl isRequired isInvalid={!!errors.firstName}>
              <FormLabel color={textColor}>First Name</FormLabel>
              <Input
                placeholder="Enter your first name"
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  if (errors.firstName) setErrors({ ...errors, firstName: '' });
                }}
                color={textColor}
                borderColor={borderColor}
                _hover={{ borderColor: useColorModeValue('gray.300', 'gray.600') }}
                _focus={{ borderColor: 'green.500', boxShadow: '0 0 0 1px #38a169' }}
              />
              <FormErrorMessage>{errors.firstName}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.lastName}>
              <FormLabel color={textColor}>Last Name</FormLabel>
              <Input
                placeholder="Enter your last name"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  if (errors.lastName) setErrors({ ...errors, lastName: '' });
                }}
                color={textColor}
                borderColor={borderColor}
                _hover={{ borderColor: useColorModeValue('gray.300', 'gray.600') }}
                _focus={{ borderColor: 'green.500', boxShadow: '0 0 0 1px #38a169' }}
              />
              <FormErrorMessage>{errors.lastName}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.emailAddress}>
              <FormLabel color={textColor}>Email Address</FormLabel>
              <Input
                type="email"
                placeholder="your.email@example.com"
                value={emailAddress}
                onChange={(e) => {
                  setEmailAddress(e.target.value);
                  if (errors.emailAddress) setErrors({ ...errors, emailAddress: '' });
                }}
                color={textColor}
                borderColor={borderColor}
                _hover={{ borderColor: useColorModeValue('gray.300', 'gray.600') }}
                _focus={{ borderColor: 'green.500', boxShadow: '0 0 0 1px #38a169' }}
              />
              <FormErrorMessage>{errors.emailAddress}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.confirmEmailAddress}>
              <FormLabel color={textColor}>Confirm Email Address</FormLabel>
              <Input
                type="email"
                placeholder="Confirm your email address"
                value={confirmEmailAddress}
                onChange={(e) => {
                  setConfirmEmailAddress(e.target.value);
                  if (errors.confirmEmailAddress) setErrors({ ...errors, confirmEmailAddress: '' });
                }}
                color={textColor}
                borderColor={borderColor}
                _hover={{ borderColor: useColorModeValue('gray.300', 'gray.600') }}
                _focus={{ borderColor: 'green.500', boxShadow: '0 0 0 1px #38a169' }}
              />
              <FormErrorMessage>{errors.confirmEmailAddress}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.roles}>
              <FormLabel color={textColor}>Team Role(s)</FormLabel>
              <CheckboxGroup 
                value={selectedRoles} 
                onChange={(values) => {
                  setSelectedRoles(values as string[]);
                  if (errors.roles) setErrors({ ...errors, roles: '' });
                }}
              >
                <Stack spacing={2} pl={2}>
                  {AVAILABLE_ROLES.map((role) => (
                    <Checkbox 
                      key={role} 
                      value={role}
                      colorScheme="green"
                      color={textColor}
                    >
                      {role}
                    </Checkbox>
                  ))}
                </Stack>
              </CheckboxGroup>
              <FormErrorMessage>{errors.roles}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired isInvalid={!!errors.message}>
              <FormLabel color={textColor}>Message to Team Owner</FormLabel>
              <Textarea
                placeholder="Tell the team owner why you want to join their team and what you can bring to the organization..."
                value={customMessage}
                onChange={(e) => {
                  setCustomMessage(e.target.value);
                  if (errors.message) setErrors({ ...errors, message: '' });
                }}
                rows={6}
                resize="vertical"
                color={textColor}
                borderColor={borderColor}
                _hover={{ borderColor: useColorModeValue('gray.300', 'gray.600') }}
                _focus={{ borderColor: 'green.500', boxShadow: '0 0 0 1px #38a169' }}
                maxLength={500}
              />
              <Text 
                fontSize="xs" 
                color={useColorModeValue('gray.500', 'gray.400')} 
                mt={1}
                textAlign="right"
              >
                {customMessage.length}/500 characters
              </Text>
              <FormErrorMessage>{errors.message}</FormErrorMessage>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={handleClose}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            colorScheme="green"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Sending..."
          >
            Send Request
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default JoinTeamModal;

